'use es6';

import { useState } from 'react';
import { getFullUrl } from 'hubspot-url-utils';
import PropTypes from 'prop-types';
import PortalIdParser from 'PortalIdParser';
import { stringify } from 'hub-http/helpers/params';
import UIModalIFrame from 'ui-addon-iframeable/host/UIModalIFrame';
import { useUpgradeEventListener } from 'ui-addon-upgrades/ums/useUpgradeEventListener';
import { useQueryParams } from '../utils/useQueryParams';
import { handleClose } from '../utils/handleClose';
import { ALERT_TYPES } from 'UIComponents/alert/AlertTypes';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_WIDTH = 600;

/** WARNINGS:

* Any addition/deletion of the props would potentially cause the client and server to be out of sync.
* Use a gate for any props updates and ungate only after making sure synchronization of client and server are confirmed.

*/

const ObjectBuilderClient = ({
  // i.e. { defaultAssociation: { objectId: '123', objectTypeId: '0-1' }, associationTypeId: 456, associationCategory: 'HUBSPOT_DEFINED' }  } }
  appInfo = {},
  // Name of the app embedding the object builder
  appName = 'object-builder-ui',
  // Default property values to prefill into the creator
  defaultProperties,
  // Enable Create tab
  createEnabledOverride,
  // Replaces the tooltip text for disabled create tab
  disabledCreateTabTooltipOverride,
  // Object type ID.
  objectTypeId,
  // Changes the form to edit an existing object, only supported for campaigns
  panelType,
  // Object ID, only used when panelType is not `create`.
  objectId,
  // Callback function called when the modal closes
  onClose,
  // unable to initialize the iframe
  onInitError = () => {},
  // any other message received from iframe (currently none)
  onMessage = () => {},
  // iframe ready
  onReady = () => {},
  // unable to establish communication with the iframe within timeout
  onReadyError = () => {},
  // Whether builder should prefill owner property
  shouldDefaultOwnerId = true,
  // Whether builder should render Create and Add Another button
  shouldRenderCreateAndAddButton = true,
  // Replaces the Create button text
  createButtonMessageOverride = undefined,
  // Whether to show the iframe
  show = true,
  // An initial default pipeline ID used to prefill fields
  initialPipelineId,
  // Enable flexible associations
  enableFlexibleAssociations,
  // a list of object property names that you'd like to appear on the create object form
  extraInitialCreatorPropertyNames,
  // a list of object property names to be hidden from default properties in the creator
  hiddenCreatorPropertyNames,
  // forces the association panel to use single select association
  forceSingleSelectAssociation,
  // a list of object property names that should be added to the association search query
  extraAssociationSearchProperties,
  // Config for an optional alert that displays below the header of the panel
  contextualAlertConfig,
  // Flag for showing embedded message for the object,
  shouldShowEmbeddedMessage,
  // Shows all records, even if already associated (used for quotes)
  showAlreadyAssociatedRecords,
  // Shows multi association options and a search bar for the selected association
  associatorSingleSelectionForMultiOptions = undefined,
  // Whether to render the UI in a condensed style
  condensedUI = false,
  // width of the iframe
  width = DEFAULT_WIDTH,
  // Handles bulk creation of objects
  bulkCreateInfo = undefined,
  // Handles split record creation
  splitRecordInfo = undefined
}) => {
  const {
    gated,
    ungated
  } = useQueryParams();
  const [iframeReady, setIframeReady] = useState(false);
  useUpgradeEventListener();
  return /*#__PURE__*/_jsx(UIModalIFrame, {
    appName: appName,
    appInfo: appInfo,
    height: "100%",
    iframePassthruProps: {
      id: 'object-builder-ui',
      'data-selenium-test': 'associate-panel-iframe',
      'data-test-id': 'object-builder-ui-iframe',
      'data-iframe-ready': iframeReady
    },
    onClose: message => {
      handleClose(message, onClose);
    },
    onInitError: onInitError,
    onMessage: onMessage,
    onReady: () => {
      setIframeReady(true);
      onReady();
    },
    onReadyError: onReadyError,
    show: show,
    embeddedPassthruProps: {
      appName,
      objectId,
      panelType,
      defaultProperties,
      createEnabledOverride,
      disabledCreateTabTooltipOverride,
      initialPipelineId,
      shouldDefaultOwnerId,
      shouldRenderCreateAndAddButton,
      createButtonMessageOverride,
      enableFlexibleAssociations,
      extraInitialCreatorPropertyNames,
      forceSingleSelectAssociation,
      extraAssociationSearchProperties,
      contextualAlertConfig,
      hiddenCreatorPropertyNames,
      shouldShowEmbeddedMessage,
      showAlreadyAssociatedRecords,
      associatorSingleSelectionForMultiOptions,
      bulkCreateInfo,
      splitRecordInfo
    },
    src: `${getFullUrl('app')}/object-builder/${PortalIdParser.get()}/${encodeURIComponent(objectTypeId)}/embed?${stringify({
      gated,
      ungated
    })}${condensedUI ? '&condensedui=true' : ''}`,
    width: width,
    use: "panel"
  });
};
ObjectBuilderClient.propTypes = {
  appInfo: PropTypes.object,
  appName: PropTypes.string,
  defaultProperties: PropTypes.object,
  objectTypeId: PropTypes.string.isRequired,
  panelType: PropTypes.string,
  objectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onInitError: PropTypes.func,
  onMessage: PropTypes.func,
  onReady: PropTypes.func,
  onReadyError: PropTypes.func,
  shouldDefaultOwnerId: PropTypes.bool,
  shouldRenderCreateAndAddButton: PropTypes.bool,
  createButtonMessageOverride: PropTypes.string,
  show: PropTypes.bool,
  initialPipelineId: PropTypes.string,
  createEnabledOverride: PropTypes.bool,
  disabledCreateTabTooltipOverride: PropTypes.string,
  enableFlexibleAssociations: PropTypes.bool,
  extraInitialCreatorPropertyNames: PropTypes.arrayOf(PropTypes.string),
  forceSingleSelectAssociation: PropTypes.bool,
  extraAssociationSearchProperties: PropTypes.arrayOf(PropTypes.string),
  contextualAlertConfig: PropTypes.shape({
    type: PropTypes.oneOf(Object.keys(ALERT_TYPES)).isRequired,
    titleText: PropTypes.string.isRequired,
    contentText: PropTypes.string.isRequired
  }),
  hiddenCreatorPropertyNames: PropTypes.arrayOf(PropTypes.string),
  shouldShowEmbeddedMessage: PropTypes.bool,
  showAlreadyAssociatedRecords: PropTypes.bool,
  associatorSingleSelectionForMultiOptions: PropTypes.shape({
    associationIdentifiers: PropTypes.object,
    atLeastOneIsRequired: PropTypes.bool,
    defaultObjectTypeId: PropTypes.string,
    objectTypeIds: PropTypes.arrayOf(PropTypes.string),
    resolverProps: PropTypes.shape({
      mirrorBackingObjectOwnerSetting: PropTypes.bool,
      originalOwnerId: PropTypes.number
    })
  }),
  condensedUI: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  bulkCreateInfo: PropTypes.shape({
    count: PropTypes.number,
    filterGroups: PropTypes.arrayOf(PropTypes.object),
    objectTypeNameFormatted: PropTypes.string,
    selectionIds: PropTypes.arrayOf(PropTypes.string),
    selectionMethod: PropTypes.string,
    selectionObjectTypeId: PropTypes.string
  }),
  splitRecordInfo: PropTypes.shape({
    splitMessageIds: PropTypes.arrayOf(PropTypes.string),
    contactVid: PropTypes.number.isRequired,
    originatingObjectProperties: PropTypes.shape({
      objectId: PropTypes.string,
      pipelineOptions: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.string,
        value: PropTypes.string
      })),
      currentStage: PropTypes.string,
      objectName: PropTypes.string
    })
  })
};
export default ObjectBuilderClient;